import type { DealStageStyle } from '../constants/dealStages';
import { glyphMarkup } from './dealGlyphs';

const DOT_DIAMETER = 12;
const DOT_RING = 2.25;
/** Room for the 16.5px mark plus the shadow's offset and blur, with nothing clipped. */
export const DEAL_DOT_CANVAS = 26;
const DOT_CENTER = DEAL_DOT_CANVAS / 2;

function slug(stage: string): string {
  return stage.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

export function dealDotIconId(stage: string): string {
  return `deal-dot-${slug(stage)}`;
}

export function dealRingIconId(stage: string): string {
  return `deal-ring-${slug(stage)}`;
}

/** The resting mark's lift off the map, matching the design's `0 1px 3px rgba(2,23,21,.3)`. */
const DOT_SHADOW = 'drop-shadow(0 1px 1.5px rgba(2, 23, 21, 0.3))';

/**
 * The resting mark: a 12px dot in the phase colour inside a 2.25px white ring — every stage, the
 * same construction. A closed deal is filled like any other; what marks it dead is losing the arc,
 * and a red that appears nowhere in the live palette.
 *
 * Two filled circles rather than a stroked one. An SVG stroke straddles its path, so it would take
 * half its width out of the fill and leave a 11.5px dot inside a ring half the design's weight —
 * invisible on a saturated phase, but on a pale fill such as Won's mint the white swallows the mark.
 */
export function buildDealDotSvg(style: DealStageStyle): string {
  const fillRadius = DOT_DIAMETER / 2;
  const ringRadius = fillRadius + DOT_RING;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${DEAL_DOT_CANVAS} ${DEAL_DOT_CANVAS}">
    <g filter="${DOT_SHADOW}">
      <circle cx="${DOT_CENTER}" cy="${DOT_CENTER}" r="${ringRadius}" fill="#fff"/>
      <circle cx="${DOT_CENTER}" cy="${DOT_CENTER}" r="${fillRadius}" fill="${style.color}"/>
    </g>
  </svg>`;
}

/** Sidebar legend swatch: the hover/open ring without the tip. */
export function buildDealSwatchSvg(style: DealStageStyle, progress: number): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${RING_CX - RING_R} ${RING_CY - RING_R} ${RING_R * 2} ${RING_R * 2}">
    ${ringBody(style, progress)}
  </svg>`;
}

/**
 * The ring and its core. The arc is drawn in `arc` rather than `color`: the vivid mint of a won
 * deal cannot carry an arc against the grey track. A won deal's core takes the mint itself, with
 * the near-black in the glyph, so the completed ring reads at a glance.
 */
function ringBody(style: DealStageStyle, progress: number): string {
  const won = style.phase === 'won';
  const arc = style.hollow ? '' : progressWedgePath(progress).replaceAll('{fill}', style.arc);
  const coreFill = won ? style.color : '#fff';
  const glyph = glyphMarkup(style.glyphId, won ? style.fg : style.glyph);

  return `<circle cx="${RING_CX}" cy="${RING_CY}" r="${RING_R}" fill="${RING_TRACK_COLOR}"/>
    ${arc}
    <circle cx="${RING_CX}" cy="${RING_CY}" r="${CORE_R}" fill="${coreFill}"/>
    <g transform="translate(${RING_CX - GLYPH_SIZE / 2} ${RING_CY - GLYPH_SIZE / 2}) scale(${GLYPH_SCALE})">${glyph}</g>`;
}

// Mirrored by RING_ICON_SIZE in useDealsStageFilter: the raster target must
// match this canvas or the mark stretches.
const RING_CANVAS_W = 40;
const RING_CANVAS_H = 44;
const RING_CX = 20;
const RING_CY = 18;
const RING_R = 16;
const CORE_R = 12;
const RING_TRACK_COLOR = '#D3DAD9';
/**
 * The ring is anchored by its bottom tip, so its centre sits this far above the deal's coordinate,
 * and spans this radius. Exported so anything drawing around the mark — the promotion burst —
 * follows the mark rather than repeating its numbers.
 */
export const DEAL_RING_CENTER_ABOVE_ANCHOR = RING_CANVAS_H - RING_CY;
export const DEAL_RING_RADIUS = RING_R;
/** The scale the ring icon is drawn at on the deals layer. */
export const DEAL_RING_ICON_SIZE = 1.1;
const GLYPH_SIZE = 14;
const GLYPH_SCALE = GLYPH_SIZE / 16;

/** `angleDeg` measured clockwise from 12 o'clock. */
function polar(cx: number, cy: number, r: number, angleDeg: number): [number, number] {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

/** Pie wedge from 12 o'clock to `progress`% clockwise, masked into a ring by the core circle. */
function progressWedgePath(progress: number): string {
  if (progress <= 0) return '';
  if (progress >= 100)
    return `<circle cx="${RING_CX}" cy="${RING_CY}" r="${RING_R}" fill="{fill}"/>`;

  const sweep = progress * 3.6;
  const [ex, ey] = polar(RING_CX, RING_CY, RING_R, sweep);
  const largeArc = sweep > 180 ? 1 : 0;
  return `<path d="M ${RING_CX} ${RING_CY} L ${RING_CX} ${RING_CY - RING_R} A ${RING_R} ${RING_R} 0 ${largeArc} 1 ${ex} ${ey} Z" fill="{fill}"/>`;
}

/** Hover/open state: a ring filled to the deal's place in the pipeline, the phase glyph in its core. */
export function buildDealRingSvg(style: DealStageStyle, progress: number): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${RING_CANVAS_W} ${RING_CANVAS_H}">
    ${ringBody(style, progress)}
  </svg>`;
}
